import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { FileDown } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Table, { type TableColumn } from '../../components/ui/Table';
import reportService, { type ReportFilters } from '../../services/report.service';

interface ReportRow {
  sl_no: number;
  case_id: string;
  uploaded_by_name: string;
  upload_date: string | null;
  section: string | null;
  sub_section: string | null;
  file_number: string | null;
  subject: string | null;
  file_count: number;
  page_count: number;
}

interface Uploader {
  user_id: number;
  name: string;
}

const PAGE_SIZE = 20;

const ReportsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    document.title = 'RRMS | Reports';
  }, []);

  // Reset to page 1 whenever a filter changes, so a narrower result set
  // doesn't leave the view stuck on a now-nonexistent later page.
  useEffect(() => {
    setPage(1);
  }, [fromDate, toDate, userId]);

  const filters: ReportFilters = {
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
    user_id: userId ? Number(userId) : undefined,
  };

  const uploadersQuery = useQuery({
    queryKey: ['reports-uploaders'],
    queryFn: () => reportService.getUploaders(),
    select: (res) => ((res.data as any)?.data ?? []) as Uploader[],
  });
  const uploaders = uploadersQuery.data ?? [];

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    setUserId('');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await reportService.exportDocumentsReport(filters);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rrms-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Failed to export the report.');
    } finally {
      setExporting(false);
    }
  };

  const query = useQuery({
    queryKey: ['reports-documents', page, filters.from_date, filters.to_date, filters.user_id],
    queryFn: () => reportService.getDocumentsReport({ page, limit: PAGE_SIZE, ...filters }),
    select: (res) => {
      const body = res.data as any;
      const items: ReportRow[] = body?.data ?? [];
      const pagination =
        body?.pagination ??
        ({
          page,
          limit: PAGE_SIZE,
          total: items.length,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        } as const);
      return { items, pagination };
    },
  });

  const items = query.data?.items ?? [];
  const pagination =
    query.data?.pagination ?? {
      page,
      limit: PAGE_SIZE,
      total: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    };

  const columns: TableColumn<ReportRow>[] = [
    {
      key: 'sl_no',
      header: 'Sl No',
      minWidth: 60,
      render: (row) => row.sl_no,
    },
    {
      key: 'uploaded_by_name',
      header: 'Upload User Name',
      minWidth: 160,
      clampLines: 2,
      tooltip: (row) => row.uploaded_by_name || '—',
      render: (row) => row.uploaded_by_name || '—',
    },
    {
      key: 'upload_date',
      header: 'Upload Date',
      minWidth: 150,
      tooltip: (row) => (row.upload_date ? format(new Date(row.upload_date), 'dd MMM yyyy, HH:mm') : '—'),
      render: (row) => (row.upload_date ? format(new Date(row.upload_date), 'dd MMM yyyy, HH:mm') : '—'),
    },
    {
      key: 'section',
      header: 'Section',
      minWidth: 150,
      clampLines: 2,
      tooltip: (row) => row.section || '—',
      render: (row) => row.section || '—',
    },
    {
      key: 'sub_section',
      header: 'Sub Section',
      minWidth: 150,
      clampLines: 2,
      tooltip: (row) => row.sub_section || '—',
      render: (row) => row.sub_section || '—',
    },
    {
      key: 'file_number',
      header: 'File Number',
      minWidth: 130,
      clampLines: 2,
      tooltip: (row) => row.file_number || '—',
      render: (row) => row.file_number || '—',
    },
    {
      key: 'subject',
      header: 'Subject',
      minWidth: 220,
      clampLines: 2,
      tooltip: (row) => row.subject || '—',
      render: (row) => row.subject || '—',
    },
    {
      key: 'file_count',
      header: 'Number of Files',
      minWidth: 130,
      align: 'center',
      render: (row) => row.file_count,
    },
    {
      key: 'page_count',
      header: 'Number of Pages',
      minWidth: 130,
      align: 'center',
      render: (row) => row.page_count,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Document upload report — one row per uploaded document."
        actions={
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary-light)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileDown size={16} />
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        }
      />

      <div className="card mb-6 p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-[var(--color-gray-700)]">From Date</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-md"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-[var(--color-gray-700)]">To Date</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-md"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-[var(--color-gray-700)]">By User</span>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-md"
            >
              <option value="">All users</option>
              {uploaders.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={handleClearFilters}
              className="rounded-md border border-[var(--color-gray-300)] px-4 py-2 text-sm font-medium text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]"
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      {query.isLoading && (
        <div className="card p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded bg-[var(--color-gray-100)]"
              style={{ marginBottom: i === 4 ? 0 : 'var(--spacing-3)' }}
            />
          ))}
        </div>
      )}

      {!query.isLoading && query.isError && (
        <div className="card p-8 text-center">
          <p className="text-md text-[var(--color-error)]">Failed to load the report. Please try again.</p>
        </div>
      )}

      {!query.isLoading && !query.isError && items.length === 0 && (
        <div className="card p-8 text-center">
          <p className="text-md font-semibold text-[var(--color-gray-700)]">No uploaded documents found.</p>
        </div>
      )}

      {!query.isLoading && !query.isError && items.length > 0 && (
        <Table
          columns={columns}
          rows={items}
          rowKey={(row) => row.case_id}
          pagination={pagination}
          onPageChange={setPage}
          minWidth={900}
        />
      )}
    </div>
  );
};

export default ReportsPage;
